import * as React from 'react';

import { ContentSection, TextField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { SalesDetailLayout } from '../../_layouts/SalesDetailLayout';

import {
  $focusedField,
  $remoteFocusPositions,
} from '~/modules/trade/_state/fieldPresence.state';
import {
  $flushOutboundCustomerAutoSave,
  $outboundCustomerFormById,
  $updateOutboundCustomerSelectField,
  $updateOutboundCustomerSelectFields,
  $updateOutboundCustomerTextField,
  DEFAULT_OUTBOUND_CUSTOMER_FORM,
  type OutboundCustomerFormState,
} from '~/modules/trade/_state/scaleTicketOutboundCustomerForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadRecord } from '~/modules/trade/types/load';
import {
  FieldFocusIndicator,
  type FocusPosition,
} from '~/shared/components/FieldFocusIndicator';
import { SelectAccount } from '~/shared/components/select-fields/SelectAccount';
import { SelectCustomerLocationPair } from '~/shared/components/select-fields/SelectCustomerLocationPair';
import { SelectableCustomerLocationPair } from '~/shared/components/select-fields/SelectCustomerLocationPairTouch';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { SelectSalesOrder } from '~/shared/components/select-fields/SelectSalesOrder';

type TextFieldKey = Extract<
  keyof OutboundCustomerFormState,
  'freightTerms' | 'shippingCarrierName' | 'scac'
>;

type SelectFieldKey = Extract<
  keyof OutboundCustomerFormState,
  'salesOrderId' | 'receivingFacilityId' | 'carrierAccountId'
>;

type OutboundTicketTabCustomerInfoProps = {
  ticket: LoadRecord;
  disabled?: boolean;
};

type FocusableFieldProps = {
  fieldId: string;
  focusPositions: FocusPosition[];
  setFocusedField: (field: string | null) => void;
  children: React.ReactNode;
};

//
// ⚛️ ⚛️ ⚛️ FOCUSABLE FIELD ⚛️ ⚛️ ⚛️
//
const FocusableField: React.FC<FocusableFieldProps> = ({
  fieldId,
  focusPositions,
  setFocusedField,
  children,
}) => {
  const handleFocus = React.useCallback(
    () => setFocusedField(fieldId),
    [setFocusedField, fieldId],
  );
  const handleBlur = React.useCallback(
    () => setFocusedField(null),
    [setFocusedField],
  );

  return (
    <FieldFocusIndicator
      fieldId={fieldId}
      focusPositions={focusPositions}
      className="@FocusableField"
    >
      <div onFocus={handleFocus} onBlur={handleBlur}>
        {children}
      </div>
    </FieldFocusIndicator>
  );
};

//
// ⚛️ ⚛️ ⚛️ OUTBOUND TICKET TAB CUSTOMER INFO ⚛️ ⚛️ ⚛️
//
export const OutboundTicketTabCustomerInfo: React.FC<
  OutboundTicketTabCustomerInfoProps
> = ({ ticket, disabled = false }) => {
  const t = useTranslations();

  // Atoms
  const focusPositions = useAtomValue($remoteFocusPositions);
  const form =
    useAtomValue($outboundCustomerFormById).get(ticket.id) ??
    DEFAULT_OUTBOUND_CUSTOMER_FORM;
  const setFocusedField = useSetAtom($focusedField);
  const updateTextField = useSetAtom($updateOutboundCustomerTextField);
  const updateSelectField = useSetAtom($updateOutboundCustomerSelectField);
  const updateSelectFields = useSetAtom($updateOutboundCustomerSelectFields);
  const flushAutoSave = useSetAtom($flushOutboundCustomerAutoSave);

  // Callbacks
  const handleTextFieldChange = React.useCallback(
    (field: TextFieldKey) => (value: string) => {
      updateTextField({ field, value, ticketId: ticket.id });
    },
    [updateTextField, ticket.id],
  );

  const handleSelectFieldChange = React.useCallback(
    (field: SelectFieldKey) => (value: string | undefined) => {
      updateSelectField({ field, value: value ?? null, ticketId: ticket.id });
    },
    [updateSelectField, ticket.id],
  );

  const handleCustomerLocationChange = React.useCallback(
    (value: string | undefined, entity?: SelectableCustomerLocationPair) => {
      const customerChanged =
        (entity?.accountId ?? null) !== form.customerAccountId;
      updateSelectFields({
        updates: {
          customerAccountId: entity?.accountId || null,
          customerAccountAddressId: value || null,
          ...(customerChanged ? { salesOrderId: null } : {}),
        },
        ticketId: ticket.id,
      });
    },
    [updateSelectFields, ticket.id, form.customerAccountId],
  );

  const handleFieldBlur = React.useCallback(() => {
    flushAutoSave(ticket.id);
  }, [flushAutoSave, ticket.id]);

  return (
    <SalesDetailLayout className="@OutboundTicketTabCustomerInfo p-0">
      <ContentSection
        title={t.salesOrdersView.wizard.contractInfo.customerInformation}
        titleClassName="font-bold text-neutral"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FocusableField
            fieldId="customerAccountAddressId"
            focusPositions={focusPositions}
            setFocusedField={setFocusedField}
          >
            <SelectCustomerLocationPair
              label={t.salesOrdersView.wizard.contractInfo.customer}
              value={form.customerAccountAddressId}
              onChange={handleCustomerLocationChange}
              isDisabled={disabled}
            />
          </FocusableField>
          <FocusableField
            fieldId="salesOrderId"
            focusPositions={focusPositions}
            setFocusedField={setFocusedField}
          >
            <SelectSalesOrder
              label={t.loadDetail.form.salesOrder}
              value={form.salesOrderId}
              onChange={handleSelectFieldChange('salesOrderId')}
              isDisabled={disabled}
              accountIds={
                form.customerAccountId ? [form.customerAccountId] : undefined
              }
            />
          </FocusableField>
        </div>
      </ContentSection>

      <ContentSection
        title={t.loadDetail.form.receivingFacility}
        titleClassName="font-bold text-neutral"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FocusableField
            fieldId="receivingFacilityId"
            focusPositions={focusPositions}
            setFocusedField={setFocusedField}
          >
            <SelectFacility
              label={t.loadDetail.form.receivingFacility}
              value={form.receivingFacilityId}
              onChange={handleSelectFieldChange('receivingFacilityId')}
              isDisabled={disabled}
            />
          </FocusableField>
        </div>
      </ContentSection>

      <ContentSection
        title={t.purchaseLoadDetail.sections.carrierInfo}
        titleClassName="font-bold text-neutral"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FocusableField
            fieldId="carrierAccountId"
            focusPositions={focusPositions}
            setFocusedField={setFocusedField}
          >
            <SelectAccount
              label={t.purchaseLoadDetail.fields.carrierAccount}
              value={form.carrierAccountId}
              onChange={handleSelectFieldChange('carrierAccountId')}
              isDisabled={disabled}
              placeholder={t.purchaseLoadDetail.placeholders.selectCarrier}
            />
          </FocusableField>

          <FocusableField
            fieldId="freightTerms"
            focusPositions={focusPositions}
            setFocusedField={setFocusedField}
          >
            <TextField
              label={t.purchaseLoadDetail.fields.freightTerms}
              value={form.freightTerms ?? ''}
              onChange={handleTextFieldChange('freightTerms')}
              onBlur={handleFieldBlur}
              isDisabled={disabled}
              placeholder={t.purchaseLoadDetail.placeholders.enterFreightTerms}
            />
          </FocusableField>

          <FocusableField
            fieldId="shippingCarrierName"
            focusPositions={focusPositions}
            setFocusedField={setFocusedField}
          >
            <TextField
              label={t.purchaseLoadDetail.fields.shippingCarrierName}
              value={form.shippingCarrierName ?? ''}
              onChange={handleTextFieldChange('shippingCarrierName')}
              onBlur={handleFieldBlur}
              isDisabled={disabled}
              placeholder={t.purchaseLoadDetail.placeholders.enterCarrierName}
            />
          </FocusableField>

          <FocusableField
            fieldId="scac"
            focusPositions={focusPositions}
            setFocusedField={setFocusedField}
          >
            <TextField
              label={t.purchaseLoadDetail.fields.scac}
              value={form.scac ?? ''}
              onChange={handleTextFieldChange('scac')}
              onBlur={handleFieldBlur}
              isDisabled={disabled}
              placeholder={t.purchaseLoadDetail.placeholders.enterScac}
            />
          </FocusableField>
        </div>
      </ContentSection>
    </SalesDetailLayout>
  );
};
